import React from "react";

import { escapeHtml } from "@/utils/htmlEscape";

/**
 * Source pattern for inline markdown tokens. A fresh `RegExp` is constructed
 * inside each parser on every call so recursion (e.g. for `**[text](url)**`)
 * does not corrupt the parent loop's `lastIndex`.
 */
export const INLINE_MD_PATTERN =
  "(\\*\\*([^*\\n]+)\\*\\*)|(__([^_\\n]+)__)|(\\[([^\\]]+)\\]\\(([^)\\s]+)\\))|(\\*([^*\\n]+)\\*)|(_([^_\\n]+)_)|(`([^`\\n]+)`)";

/**
 * Whitelist of URL schemes safe to render as `<a href>`.
 * Anything else (e.g. `javascript:`) is rendered as plain text.
 */
export const SAFE_URL_REGEX = /^(https?:|mailto:|\/|#|\.\.?\/)/i;

/**
 * Render a plain string with inline markdown (bold, italic, inline code,
 * links) as React nodes. Anything that doesn't match a known marker is
 * returned verbatim. Bold/italic/link contents are recursively parsed so
 * patterns like `**[text](url)**` render correctly.
 */
export function renderInlineMarkdown(text: string): React.ReactNode {
  if (!text) return text;

  const regex = new RegExp(INLINE_MD_PATTERN, "g");
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2] !== undefined) {
      nodes.push(<strong key={key++}>{renderInlineMarkdown(match[2])}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<strong key={key++}>{renderInlineMarkdown(match[4])}</strong>);
    } else if (match[6] !== undefined && match[7] !== undefined) {
      const linkText = match[6];
      const linkUrl = match[7];

      if (SAFE_URL_REGEX.test(linkUrl)) {
        nodes.push(
          <a
            key={key++}
            className="text-primary-600 underline hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            href={linkUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {renderInlineMarkdown(linkText)}
          </a>,
        );
      } else {
        nodes.push(linkText);
      }
    } else if (match[9] !== undefined) {
      nodes.push(<em key={key++}>{renderInlineMarkdown(match[9])}</em>);
    } else if (match[11] !== undefined) {
      nodes.push(<em key={key++}>{renderInlineMarkdown(match[11])}</em>);
    } else if (match[13] !== undefined) {
      nodes.push(<code key={key++}>{match[13]}</code>);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  if (nodes.length === 0) return text;

  return <>{nodes}</>;
}

/**
 * Render a plain string with inline markdown (bold, italic, inline code,
 * links) as an HTML string with all literal characters escaped. Mirrors
 * `renderInlineMarkdown` for non-React contexts (e.g. PDF table cells built
 * by string concatenation before being captured to image / printed).
 */
export function inlineMarkdownToHtml(text: string): string {
  if (!text) return "";

  const regex = new RegExp(INLINE_MD_PATTERN, "g");
  let out = "";
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out += escapeHtml(text.slice(lastIndex, match.index));
    }

    if (match[2] !== undefined) {
      out += `<strong>${inlineMarkdownToHtml(match[2])}</strong>`;
    } else if (match[4] !== undefined) {
      out += `<strong>${inlineMarkdownToHtml(match[4])}</strong>`;
    } else if (match[6] !== undefined && match[7] !== undefined) {
      const linkText = match[6];
      const linkUrl = match[7];

      if (SAFE_URL_REGEX.test(linkUrl)) {
        out +=
          `<a href="${escapeHtml(linkUrl)}" target="_blank" rel="noopener noreferrer">` +
          `${inlineMarkdownToHtml(linkText)}</a>`;
      } else {
        out += escapeHtml(linkText);
      }
    } else if (match[9] !== undefined) {
      out += `<em>${inlineMarkdownToHtml(match[9])}</em>`;
    } else if (match[11] !== undefined) {
      out += `<em>${inlineMarkdownToHtml(match[11])}</em>`;
    } else if (match[13] !== undefined) {
      out += `<code>${escapeHtml(match[13])}</code>`;
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    out += escapeHtml(text.slice(lastIndex));
  }

  return out;
}

/**
 * Strip inline markdown markers, leaving the underlying text.
 * Useful for tooltip titles or other plain-text contexts.
 */
export function stripInlineMarkdown(text: string): string {
  if (!text) return text;

  return text
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1")
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1");
}
